/** Why one record was left off a chat command card. */
export const ChatCommandSkipReason = {
  NOT_FOUND: 'NOT_FOUND',
  /** The record's current state does not allow the command, as the UI would not offer it. */
  NOT_ELIGIBLE: 'NOT_ELIGIBLE',
  /** Cancelled or archived — read-only. */
  INACTIVE: 'INACTIVE'
} as const;

export type ChatCommandSkipReason =
  (typeof ChatCommandSkipReason)[keyof typeof ChatCommandSkipReason];

/** Shape of a chat confirmation card payload, read by the generic card renderers. */
export const ChatProposalKind = {
  /** One command applied to a list of records. */
  COMMAND: 'command',
  /** Field changes to one record, shown as before → after. */
  CHANGE: 'change'
} as const;

export type ChatProposalKind = (typeof ChatProposalKind)[keyof typeof ChatProposalKind];

/** Kind of the values a change card row shows translated in place of its text. */
export const ChatFieldValueKind = {
  /** Integration entity types, e.g. `invoice`. */
  ENTITY_TYPE: 'entityType',
  /** Sync directions, `IN` / `OUT`. */
  SYNC_DIRECTION: 'syncDirection',
  /** A yes/no flag, `true` / `false`. */
  YES_NO: 'yesNo',
  /** A schedule entry: `mode|cron|expr|timezone`, `mode|interval|minutes` or `mode|event`. */
  SCHEDULE: 'schedule'
} as const;

export type ChatFieldValueKind = (typeof ChatFieldValueKind)[keyof typeof ChatFieldValueKind];

/** Action type of every chat catalog card; the web-app needs a title for each. */
export const CHAT_CARD_ACTION_TYPES = [
  'accept-invoice-warnings',
  'accept-purchase-order-warnings',
  'accept-requisition-warnings',
  'activate-jobs',
  'approve-invoices-accepting-warnings',
  'archive-goods-receipts',
  'archive-purchase-orders',
  'archive-requisitions',
  'buy-credit-pack',
  'cancel-goods-receipts',
  'cancel-invoices',
  'cancel-purchase-orders',
  'cancel-requisitions',
  'change-approval-policy',
  'change-automation-rules',
  'change-connector-schedules',
  'change-connector-settings',
  'change-document-output',
  'change-invoice-automation-switches',
  'change-number-series',
  'change-org-unit-members',
  'change-validation-policy',
  'close-jobs',
  'code-invoices',
  'complete-invoices',
  'complete-purchase-orders',
  'complete-requisitions',
  'confirm-delivery-without-receipt',
  'confirm-goods-receipts',
  'confirm-matches',
  'deactivate-cost-centers',
  'deactivate-org-units',
  'deactivate-products',
  'delete-connection',
  'delete-connector',
  'delete-documents',
  'delete-email-templates',
  'delete-goods-receipts',
  'delete-invoices',
  'delete-jobs',
  'delete-partners',
  'delete-products',
  'delete-purchase-orders',
  'delete-requisitions',
  'discard-quarantined-emails',
  'dispatch-purchase-order',
  'draft-cost-center',
  'draft-goods-receipt',
  'draft-job',
  'draft-org-unit',
  'draft-product',
  'draft-purchase-order',
  'draft-requisition',
  'edit-invoice',
  'link-invoices',
  'manage-mailbox',
  'mark-notifications-read',
  'move-invoices-to-export',
  'order-from-requisition',
  'pair-invoice-lines',
  'receive-from-purchase-order',
  'reject-match-links',
  'reject-matches',
  'release-quarantined-emails',
  'rematch-invoices',
  'remove-invoice-attachments',
  'reply-to-thread',
  'reprocess-documents',
  'repush-attachments',
  'rerun-analysis',
  'resend-sync-changes',
  'reset-approval-policy',
  'reset-validation-policy',
  'retry-stuck-sync-events',
  'return-invoices-to-draft',
  'return-purchase-orders-to-draft',
  'return-requisitions-to-draft',
  'set-approvers',
  'set-org-unit-manager',
  'setup-connector',
  'submit-invoices',
  'suspend-jobs',
  'switch-connector',
  'trust-senders',
  'unarchive-goods-receipts',
  'unarchive-purchase-orders',
  'unarchive-requisitions',
  'unlink-invoices',
  'unmatch-invoice-lines',
  'untrust-senders'
] as const;

export type ChatCardActionType = (typeof CHAT_CARD_ACTION_TYPES)[number];
